// eslint-disable-next-line @typescript-eslint/no-var-requires
const jspm3 = process.env.VUE_APP_DISABLE_PRINT_MANAGER === '1' ? null : require('jsprintmanager3').default;
// eslint-disable-next-line @typescript-eslint/no-var-requires
const jspm4 = process.env.VUE_APP_DISABLE_PRINT_MANAGER === '1' ? null : require('jsprintmanager4').default;
// eslint-disable-next-line @typescript-eslint/no-var-requires
const jspm5 = process.env.VUE_APP_DISABLE_PRINT_MANAGER === '1' ? null : require('jsprintmanager5');
// eslint-disable-next-line @typescript-eslint/no-var-requires
const jspm9 = process.env.VUE_APP_DISABLE_PRINT_MANAGER === '1' ? null : require('jsprintmanager9');

enum PrintMangerVersion {
    v3_0 = '3.0',
    v4_0 = '4.0',
    v5_0 = '5.0',
    v9_0 = '9.0',
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type JspmInstance = any;

class PrintManger {
    protected _instance: JspmInstance;
    protected _version = '';
    protected _connected = false;

    constructor(instance: JspmInstance, version: string) {
        this._instance = instance;
        this._version = version;

        if (version !== PrintMangerVersion.v3_0 && this._instance?.JSPrintManager) {
            const base = process.env.VUE_APP_DEVPROD === 'DEVELOPMENT'
                ? process.env.VUE_APP_API_BASE_URL
                : window.location.origin;

            this._instance.JSPrintManager.license_url = base + '/jspm_license';
        }
    }

    public get manager(): JspmInstance {
        return this._instance;
    }

    public get version(): string {
        return this._version;
    }

    public hasConnected(): boolean {
        return this._connected;
    }

    public connect(): PrintManger {
        this._connected = true;
        return this;
    }
}

class PrintManagerService {
    protected _connection: PrintManger | null = null;
    protected _jspms: PrintManger[];
    protected _retryTimer: number | null = null;

    constructor() {
        this._jspms = [
            new PrintManger(jspm9, PrintMangerVersion.v9_0),
            new PrintManger(jspm5, PrintMangerVersion.v5_0),
            new PrintManger(jspm4, PrintMangerVersion.v4_0),
            new PrintManger(jspm3, PrintMangerVersion.v3_0),
        ].filter(({ manager }) => manager);
    }

    public get printer(): PrintManger | null {
        return this._connection;
    }

    private _startInstance(jspm: PrintManger): void {
        const { JSPrintManager, WSStatus } = jspm.manager;

        // Keep auto_reconnect off until we know this version won.
        // Enabling it upfront causes stopped losers to keep reconnecting.
        JSPrintManager.auto_reconnect = false;

        const startPromise: Promise<void> = JSPrintManager.start(true);

        startPromise
            .then(() => {
                if (!this._connection) {
                    this._connection = jspm.connect();
                    JSPrintManager.auto_reconnect = true; // only the winner gets auto-reconnect
                    console.log('connected to jspm version', jspm.version, jspm);
                } else {
                    console.warn('already connected to a version, stopping', jspm.version);
                    JSPrintManager.auto_reconnect = false;
                    JSPrintManager.stop();
                }

                this._jspms.forEach((j) => {
                    if (!j.hasConnected()) {
                        console.log('stopping auto reconnect for version', j.version);

                        const { JSPrintManager: jpm } = j.manager;
                        jpm.auto_reconnect = false;
                        if (jpm.WS) {
                            jpm.WS.autoReconnect = false;
                        }
                        jpm.stop();
                    }
                });

                // Set status handler after start so WS is guaranteed to exist (required for JSPM9)
                if (JSPrintManager.WS) {
                    JSPrintManager.WS.onStatusChanged = (): void => {
                        const status = WSStatus[JSPrintManager.websocket_status];
                        console.log('status for version', jspm.version, '->', status);
                    };
                }
            })
            .catch((err: string) => {
                console.error('failed to connect', err);

                if (this._connection) {
                    JSPrintManager.auto_reconnect = false;
                }
            });

        // For older versions WS is available immediately; for JSPM9 it is set after start()
        if (JSPrintManager.WS) {
            JSPrintManager.WS.onStatusChanged = (): void => {
                const status = WSStatus[JSPrintManager.websocket_status];
                console.log('status for version', jspm.version, '->', status);
            };
        }
    }

    public start(): void {
        for (const jspm of this._jspms) {
            this._startInstance(jspm);
        }

        // Retry every 15s until one version connects, then stop.
        if (this._retryTimer === null && this._jspms.length > 0) {
            this._retryTimer = window.setInterval(() => {
                if (this._connection) {
                    window.clearInterval(this._retryTimer!);
                    this._retryTimer = null;
                    return;
                }
                console.log('[PrintManager] No connection yet, retrying...');
                for (const jspm of this._jspms) {
                    if (!jspm.hasConnected()) {
                        this._startInstance(jspm);
                    }
                }
            }, 15000);
        }
    }

    public printerStatus(): string {
        if (this._connection) {
            const { JSPrintManager, WSStatus } = this._connection.manager;
            return WSStatus[JSPrintManager.websocket_status] ?? '-';
        }

        return '-';
    }
}

export default new PrintManagerService();
